/**
 * GA4 wrapper. All analytics calls in the app must go through this module.
 *
 * - `gtag` is loaded by index.html and assigned to window.gtag.
 * - We default consent to denied (in index.html) and flip to granted only
 *   after the user passes terms acceptance.
 * - We never send PII. user_id is the Supabase auth uid only.
 */

export const GA_MEASUREMENT_ID = "G-R4J0RL8RJJ";

type GtagFn = (...args: unknown[]) => void;

function getGtag(): GtagFn | null {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { gtag?: GtagFn }).gtag;
  return typeof g === "function" ? g : null;
}

export function trackPageView(path: string, title?: string) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", name, params);
}

export interface AnalyticsUserProps {
  userId: string;
  role?: string | null;
  grade?: string | null;
}

export function setAnalyticsUser({ userId, role, grade }: AnalyticsUserProps) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("config", GA_MEASUREMENT_ID, {
    user_id: userId,
    send_page_view: false,
    anonymize_ip: true,
  });
  const userProps: Record<string, unknown> = {};
  if (role) userProps.role = role;
  if (grade) userProps.grade = grade;
  if (Object.keys(userProps).length) {
    gtag("set", "user_properties", userProps);
  }
  // Treat students as kids by default — disable signals & personalized ads.
  if (role === "user" || role === "student") {
    gtag("set", "allow_google_signals", false);
    gtag("set", "allow_ad_personalization_signals", false);
  }
}

export function clearAnalyticsUser() {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("config", GA_MEASUREMENT_ID, {
    user_id: undefined,
    send_page_view: false,
    anonymize_ip: true,
  });
}

export function setAnalyticsConsent(granted: boolean) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
  });
}
